import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, type LucideIcon } from 'lucide-react';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'rounded-md font-medium whitespace-nowrap',
    'transition-colors duration-base ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-forest text-cream hover:bg-forest-2 active:bg-forest-3',
        secondary:
          'bg-bg-primary text-fg-primary border border-border hover:bg-bg-tertiary active:bg-bg-tertiary',
        ghost: 'bg-transparent text-fg-primary hover:bg-bg-tertiary active:bg-bg-tertiary',
        danger:
          'bg-terracotta-2 text-cream hover:bg-terracotta active:bg-terracotta-2',
        // Legacy aliases — accepted to keep existing call sites working until
        // the #228 vocabulary sweep migrates them. New code MUST use the four
        // names above per ds-handoff.jsx APIPage.
        default: 'bg-forest text-cream hover:bg-forest-2 active:bg-forest-3',
        destructive:
          'bg-terracotta-2 text-cream hover:bg-terracotta active:bg-terracotta-2',
        outline:
          'bg-bg-primary text-fg-primary border border-border hover:bg-bg-tertiary active:bg-bg-tertiary',
        link: 'bg-transparent text-forest underline-offset-4 hover:underline px-0 h-auto',
      },
      size: {
        sm: 'h-8 px-3 text-small',
        md: 'h-10 px-4 text-body',
        lg: 'h-12 px-5 text-body',
        // Legacy aliases.
        default: 'h-10 px-4 text-body',
        icon: 'h-10 w-10 p-0',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    ButtonVariantProps {
  /** Lucide icon component rendered before children. Replaced by a spinner when `loading`. */
  leadingIcon?: LucideIcon;
  /** Lucide icon component rendered after children. */
  trailingIcon?: LucideIcon;
  /** When true, swaps the leading icon for a spinner and disables interaction. */
  loading?: boolean;
  /** Render as a different element (Radix Slot) — preserved for legacy call sites. */
  asChild?: boolean;
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      leadingIcon: LeadingIcon,
      trailingIcon: TrailingIcon,
      loading = false,
      disabled,
      asChild = false,
      children,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const iconClass = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
    const mergedClassName = cn(buttonVariants({ variant, size, fullWidth }), className);

    // Icon-only buttons require an aria-label for a11y; warn in dev if missing.
    if (
      import.meta.env.DEV &&
      !asChild &&
      !ariaLabel &&
      !children &&
      (LeadingIcon || TrailingIcon || size === 'icon')
    ) {
      console.warn(
        '[Button] Icon-only buttons must provide an `aria-label` for accessibility.',
      );
    }

    // asChild path: Radix Slot requires exactly one React element child, so we
    // forward children directly. leadingIcon/trailingIcon/loading are not
    // supported in this mode — the wrapped element owns its own content.
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={mergedClassName}
          aria-label={ariaLabel}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={mergedClassName}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        aria-label={ariaLabel}
        {...props}
      >
        {loading ? (
          <Loader2 className={cn(iconClass, 'animate-spin')} aria-hidden="true" />
        ) : LeadingIcon ? (
          <LeadingIcon className={iconClass} aria-hidden="true" />
        ) : null}
        {children}
        {!loading && TrailingIcon ? (
          <TrailingIcon className={iconClass} aria-hidden="true" />
        ) : null}
      </button>
    );
  },
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
